import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input.tsx';
import { UserSelector } from '@/components/widget/user-selector';
import { useNavigateWithContentFlush, useUser } from '@/hooks';
import { useDrives } from '@/hooks/useDrivesQueries';
import { useMemberExternalAccounts } from '@/hooks/useMemberExternalAccountQueries';
import { useDebounceWithLoading } from '@/lib/utils';
import { useSearchStore } from '@/store/searchStore';
import { buildQueryString } from '@/utils/search-utils';
import { Loader2, Search, SlidersHorizontal, X } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export function ExploreSearchBar() {
  const {
    query,
    setQuery,
    filters,
    setFilters,
    inputValue,
    setInputValue,
    resetInputValue,
    allTabFilters,
    activeSavedFilterId,
    clearActiveSavedFilter
  } = useSearchStore();
  const navigate = useNavigateWithContentFlush();
  const { data: memberContext } = useUser();
  const memberId = memberContext?.selectedMember?.id ?? 0;
  const accountsQuery = useMemberExternalAccounts(memberId);
  const mailAccounts = (accountsQuery.data?.items ?? []).filter((account) => account.isActive);
  const drivesQuery = useDrives(memberId);
  const drives = drivesQuery.data?.drives ?? [];
  const [isFilterPaneOpen, setIsFilterPaneOpen] = useState(false);
  const [filterPaneStyles, setFilterPaneStyles] = useState({});
  const searchInputRef = useRef<HTMLDivElement>(null);
  const filterPaneRef = useRef<HTMLDivElement>(null);
  const { debouncedValue, isLoading } = useDebounceWithLoading(inputValue, 300);
  const tabOptions = allTabFilters || [];
  useEffect(() => {
    // Guard against stale debounce writes overriding immediate tab clicks.
    if (debouncedValue !== inputValue) {
      return;
    }
    if (debouncedValue !== query) {
      setQuery(debouncedValue);
    }
  }, [debouncedValue, inputValue, query, setQuery]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleCreatedByChange = (userIds: string[]) => {
    setFilters({ ...filters, createdBy: userIds.length > 0 ? userIds : undefined });
  };

  const applyFilters = () => {
    clearActiveSavedFilter();
    const newQuery = buildQueryString(filters);
    setInputValue(newQuery, navigate);
    setIsFilterPaneOpen(false);
  };

  const updateFilterPaneStyles = () => {
    if (searchInputRef.current) {
      const { offsetHeight, offsetWidth, offsetLeft } = searchInputRef.current;
      setFilterPaneStyles({
        top: offsetHeight + 4,
        left: offsetLeft,
        width: offsetWidth,
      });
    }
  };

  useEffect(() => {
    updateFilterPaneStyles();
    window.addEventListener('resize', updateFilterPaneStyles);

    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterPaneRef.current &&
        !filterPaneRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setIsFilterPaneOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', updateFilterPaneStyles);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Fragment>
      <div className="relative w-full" ref={searchInputRef}>
        {isLoading ? (
          <Loader2 className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground animate-spin" />
        ) : (
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        )}
        <Input
          data-testid="explore-search-input"
          type="search"
          placeholder="Search..."
          className="w-full pl-8 pr-10"
          value={inputValue}
          onChange={(e) => {
            if (activeSavedFilterId !== null) {
              clearActiveSavedFilter();
            }
            setInputValue(e.target.value, navigate);
          }}
          noClearButton
        />
        {inputValue && (
          <X
            className="absolute right-6 top-2.5 h-4 w-4 mr-2 text-muted-foreground cursor-pointer"
            onClick={() => {
              clearActiveSavedFilter();
              setQuery('');
              resetInputValue(navigate);
            }}
          />
        )}
        <SlidersHorizontal
          data-testid="explore-filter-dropdown"
          className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground cursor-pointer"
          onClick={() => {
            setIsFilterPaneOpen(!isFilterPaneOpen);
            updateFilterPaneStyles();
          }}
        />
      </div>

      {isFilterPaneOpen && (
        <div
          ref={filterPaneRef}
          className="absolute bg-background p-4 border rounded shadow-md"
          style={{
            ...filterPaneStyles,
            position: 'absolute',
            zIndex: 1000,
          }}
        >
          <div className="flex flex-col space-y-4">
            <div className="flex items-center">
              <label className="text-sm text-muted-foreground w-24">Tab:</label>
              <select
                className="w-full border rounded-md px-2 py-1 text-sm text-muted-foreground "
                value={filters.tab || ''}
                onChange={(e) => handleFilterChange('tab', e.target.value)}
              >
                <option value="">Select Tab</option>
                {tabOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <label className="text-sm text-muted-foreground w-24">Include:</label>
              <Input
                type="text"
                placeholder="Include String"
                className="w-full"
                value={filters.include || ''}
                onChange={(e) => handleFilterChange('include', e.target.value)}
              />
            </div>
            <div className="flex items-center">
              <label className="text-sm text-muted-foreground w-24">Exclude:</label>
              <Input
                type="text"
                placeholder="Exclude String"
                className="w-full"
                value={filters.exclude || ''}
                onChange={(e) => handleFilterChange('exclude', e.target.value)}
              />
            </div>
            <div className="flex items-center">
              <label className="text-sm text-muted-foreground w-24">After:</label>
              <Input
                type="date"
                className="w-full"
                value={filters.after || ''}
                onChange={(e) => handleFilterChange('after', e.target.value)}
              />
            </div>
            <div className="flex items-center">
              <label className="text-sm text-muted-foreground w-24">Before:</label>
              <Input
                type="date"
                className="w-full"
                value={filters.before || ''}
                onChange={(e) => handleFilterChange('before', e.target.value)}
              />
            </div>
            <div className="flex items-center">
              <label className="text-sm text-muted-foreground w-24">Created By:</label>
              <UserSelector
                selectedUserIds={filters.createdBy || []}
                onSelectionChange={handleCreatedByChange}
                placeholder="Select users..."
              />
            </div>
            <div className="flex items-start">
              <label className="text-sm text-muted-foreground w-24">Mailbox:</label>
              <div className="min-w-0 flex-1 space-y-1">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={filters.mailbox === true}
                    disabled={mailAccounts.length === 0}
                    onCheckedChange={(checked) =>
                      setFilters({ ...filters, mailbox: checked === true ? true : undefined })
                    }
                  />
                  Search my connected mailboxes too
                </label>
                {mailAccounts.length > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {mailAccounts
                      .map((account) => `${account.productLabel} ${account.externalEmail}`)
                      .join(', ')}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Connect Gmail or Outlook in{' '}
                    <Link to="/settings/integrations" className="underline">
                      Integrations
                    </Link>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-start">
              <label className="text-sm text-muted-foreground w-24">Drives:</label>
              <div className="min-w-0 flex-1 space-y-1">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={filters.drives === true}
                    disabled={drives.length === 0}
                    onCheckedChange={(checked) =>
                      setFilters({ ...filters, drives: checked === true ? true : undefined })
                    }
                  />
                  Search connected drives too (by file name)
                </label>
                {drives.length > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {drives.map((drive) => `${drive.productLabel} ${drive.name}`).join(', ')}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No drives connected; a hub admin adds them in{' '}
                    <Link to="/settings/integrations" className="underline">
                      Integrations
                    </Link>
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button
              className="ml-auto px-4 py-2 rounded"
              onClick={applyFilters}
            >
              Search
            </Button>
          </div>
        </div>
      )}
    </Fragment>
  );
}
